import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useWallet } from "@solana/wallet-adapter-react";
import { formatWalletAddress } from "@/solana/utils/wallet-utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowDownCircleIcon,
  ArrowUpCircleIcon,
  ArrowRightLeft,
  CheckCircle2Icon,
  CheckIcon,
  CopyIcon,
  ChevronDownIcon,
  ClockIcon,
  CreditCardIcon,
  KeyIcon,
  LayoutDashboardIcon,
  Loader2Icon,
  LogOutIcon,
  StarIcon,
  EyeIcon,
  EyeOffIcon,
  ExternalLink,
  BookmarkIcon,
  MailIcon,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { LoginModal } from "@/components/auth/login-modal";
import { LinkEmailModal } from "@/components/auth/link-email-modal";
import { WithdrawalsApi, WithdrawalAddressesApi } from "@/backend/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useAccountSummary } from "@/solana/hooks/use-account-summary";
import { formatCurrency, isValidSolanaAddress } from "@/lib/utils";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { usePrivyAuth } from "@/backend/use-privy-auth";
import { usePrivacyMode } from "@/hooks/use-privacy-mode";
import type { SavedWithdrawalAddress } from "@/backend/types";

// Site's green color - brand color for positive actions
const SITE_GREEN = "#2ebd85";

// =============================================================================
// ASSET & CHAIN CONFIGURATION (for withdrawal modal)
// =============================================================================

type Asset = {
  id: string;
  name: string;
  symbol: string;
  enabled: boolean;
  comingSoon?: boolean;
};

type Chain = {
  id: string;
  name: string;
  enabled: boolean;
  comingSoon?: boolean;
  speed?: "fast" | "medium" | "slow";
};

const SUPPORTED_ASSETS: Asset[] = [
  { id: "usdc", name: "USD Coin", symbol: "USDC", enabled: true },
  { id: "usdt", name: "Tether", symbol: "USDT", enabled: false, comingSoon: true },
];

const SUPPORTED_CHAINS: Chain[] = [
  { id: "solana", name: "Solana", enabled: true, speed: "fast" },
  { id: "ethereum", name: "Ethereum", enabled: false, comingSoon: true },
  { id: "arbitrum", name: "Arbitrum", enabled: false, comingSoon: true },
];

// =============================================================================
// ICONS
// =============================================================================

const SolanaIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 128 128" fill="none">
    <defs>
      <linearGradient id="solana-grad-w-a" x1="94" y1="105" x2="45" y2="20" gradientUnits="userSpaceOnUse">
        <stop stopColor="#00FFA3" /><stop offset="1" stopColor="#DC1FFF" />
      </linearGradient>
      <linearGradient id="solana-grad-w-b" x1="76" y1="115" x2="28" y2="30" gradientUnits="userSpaceOnUse">
        <stop stopColor="#00FFA3" /><stop offset="1" stopColor="#DC1FFF" />
      </linearGradient>
      <linearGradient id="solana-grad-w-c" x1="85" y1="110" x2="37" y2="25" gradientUnits="userSpaceOnUse">
        <stop stopColor="#00FFA3" /><stop offset="1" stopColor="#DC1FFF" />
      </linearGradient>
    </defs>
    <path d="M36.2 85.5c.9-.9 2.1-1.4 3.4-1.4h58.8c2.1 0 3.2 2.6 1.7 4.1l-11.6 11.6c-.9.9-2.1 1.4-3.4 1.4H26.3c-2.1 0-3.2-2.6-1.7-4.1l11.6-11.6z" fill="url(#solana-grad-w-a)" />
    <path d="M36.2 28.8c.9-.9 2.2-1.4 3.4-1.4h58.8c2.1 0 3.2 2.6 1.7 4.1L88.5 43.1c-.9.9-2.1 1.4-3.4 1.4H26.3c-2.1 0-3.2-2.6-1.7-4.1l11.6-11.6z" fill="url(#solana-grad-w-b)" />
    <path d="M88.5 56.9c-.9-.9-2.1-1.4-3.4-1.4H26.3c-2.1 0-3.2 2.6-1.7 4.1l11.6 11.6c.9.9 2.1 1.4 3.4 1.4h58.8c2.1 0 3.2-2.6 1.7-4.1L88.5 56.9z" fill="url(#solana-grad-w-c)" />
  </svg>
);

const EthereumIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 128 128" fill="none">
    <path d="M64 8L25 65l39 23 39-23L64 8z" fill="#627EEA" />
    <path d="M64 8v42l39 15L64 8z" fill="#627EEA" fillOpacity="0.6" />
    <path d="M64 96l39-23-39 47V96z" fill="#627EEA" />
    <path d="M64 96v24l-39-47 39 23z" fill="#627EEA" fillOpacity="0.6" />
    <path d="M64 88l39-23-39-15v38z" fill="#627EEA" fillOpacity="0.2" />
    <path d="M25 65l39 23V50L25 65z" fill="#627EEA" fillOpacity="0.6" />
  </svg>
);

const ArbitrumIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 128 128" fill="none">
    <circle cx="64" cy="64" r="56" fill="#213147" />
    <path d="M76.8 88.5L64 55.7 51.2 88.5h-9.6L64 40l22.4 48.5h-9.6z" fill="#28A0F0" />
    <path d="M64 55.7l12.8 32.8h-9.6L64 79.5l-3.2 9h-9.6L64 55.7z" fill="white" />
  </svg>
);

const UsdcIcon = ({ className }: { className?: string }) => (
  <img src="/icons/tokens/usdc.svg" alt="USDC" className={className} />
);

const UsdtIcon = ({ className }: { className?: string }) => (
  <img src="/icons/tokens/usdt.svg" alt="USDT" className={className} />
);

const ChainIcon = ({ chainId, className }: { chainId: string; className?: string }) => {
  switch (chainId) {
    case "solana":
      return <SolanaIcon className={className} />;
    case "ethereum":
      return <EthereumIcon className={className} />;
    case "arbitrum":
      return <ArbitrumIcon className={className} />;
    default:
      return null;
  }
};

const AssetIcon = ({ assetId, className }: { assetId: string; className?: string }) => {
  switch (assetId) {
    case "usdc":
      return <UsdcIcon className={className} />;
    case "usdt":
      return <UsdtIcon className={className} />;
    default:
      return null;
  }
};

type HeaderWalletButtonProps = {
  onOpenDeposit?: () => void;
};

export const HeaderWalletButton = ({ onOpenDeposit }: HeaderWalletButtonProps) => {
  const { publicKey, wallet } = useWallet();
  const { authMethod, isAuthenticated, userId, email, accessToken, logout, loginMethod } = useUnifiedAuth();
  const {
    fundWallet: privyFundWallet,
    solanaWalletAddress: privyWalletAddress,
    exportWallet: privyExportWallet
  } = usePrivyAuth();
  const queryClient = useQueryClient();
  const { summary } = useAccountSummary();

  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [linkEmailOpen, setLinkEmailOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Asset & Chain selection (future-proof, currently only USDC on Solana)
  const [selectedAsset, setSelectedAsset] = useState("usdc");
  const [selectedChain, setSelectedChain] = useState("solana");

  // Email user withdrawal address state
  const [destinationAddressInput, setDestinationAddressInput] = useState("");
  const [savedAddresses, setSavedAddresses] = useState<SavedWithdrawalAddress[]>([]);
  const [selectedSavedAddressId, setSelectedSavedAddressId] = useState<string>("");
  const [saveThisAddress, setSaveThisAddress] = useState(false);
  const [addressLabel, setAddressLabel] = useState("");
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);

  // Pending withdrawal state
  const [pendingWithdrawal, setPendingWithdrawal] = useState<any | null>(null);

  // Copy feedback state - shows checkmark icon temporarily after copying
  const [walletCopied, setWalletCopied] = useState(false);
  const [uidCopied, setUidCopied] = useState(false);

  // Privacy mode - hide sensitive balance information (industry standard feature)
  const { privacyMode, togglePrivacyMode } = usePrivacyMode();

  // For wallet users, use wallet address; for email users, use Supabase user ID
  const effectiveUserId = userId;

  // Get destination address based on auth method
  const getDestinationAddress = () => {
    if (authMethod === "wallet") {
      return publicKey?.toBase58() || null;
    }
    // For email/privy users, use selected saved address or manual input
    if (selectedSavedAddressId && selectedSavedAddressId !== "new") {
      const saved = savedAddresses.find((a) => a.id === selectedSavedAddressId);
      return saved?.address || null;
    }
    return destinationAddressInput || null;
  };

  const equityValue =
    summary ? summary.collateral + summary.unrealizedPnl : 0;

  // Load saved addresses when dialog opens (for privy users)
  useEffect(() => {
    if (withdrawOpen && authMethod === "privy" && effectiveUserId) {
      setIsLoadingAddresses(true);
      WithdrawalAddressesApi.listAddresses()
        .then((addresses) => {
          setSavedAddresses(addresses);
          // If there are saved addresses, select the first one by default
          if (addresses.length > 0) {
            setSelectedSavedAddressId(addresses[0].id);
          } else {
            setSelectedSavedAddressId("new");
          }
        })
        .catch((error) => {
          console.error("Failed to load saved addresses:", error);
        })
        .finally(() => {
          setIsLoadingAddresses(false);
        });
    }
  }, [withdrawOpen, authMethod, effectiveUserId]);

  // Fetch pending withdrawals when dialog opens
  useEffect(() => {
    if (withdrawOpen && effectiveUserId) {
      WithdrawalsApi.listWithdrawals()
        .then((withdrawals) => {
          // Find the most recent pending/processing withdrawal
          const pending = withdrawals.find(
            (w) => w.status === "pending" || w.status === "processing" || w.status === "approved"
          );
          setPendingWithdrawal(pending || null);
        })
        .catch((error) => {
          console.error("Failed to load withdrawals:", error);
        });
    }
  }, [withdrawOpen, effectiveUserId]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!withdrawOpen) {
      setDestinationAddressInput("");
      setSelectedSavedAddressId("");
      setSaveThisAddress(false);
      setAddressLabel("");
      setAmount("");
    }
  }, [withdrawOpen]);

  // Minimum withdrawal amount in USD
  const MINIMUM_WITHDRAWAL = 10;

  const handleWithdraw = async () => {
    const destAddress = getDestinationAddress();
    if (!effectiveUserId || !destAddress || !amount) return;

    // Validate address for email/privy users
    if (authMethod === "privy" && !isValidSolanaAddress(destAddress)) {
      toast.error("Please enter a valid Solana wallet address");
      return;
    }

    const withdrawAmount = parseFloat(amount);
    if (isNaN(withdrawAmount) || withdrawAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    // Enforce minimum withdrawal amount
    if (withdrawAmount < MINIMUM_WITHDRAWAL) {
      toast.error(`Minimum withdrawal is $${MINIMUM_WITHDRAWAL} USDC`);
      return;
    }

    setIsLoading(true);
    try {
      // Save address if checkbox is checked (for email/privy users with new address)
      if (
        authMethod === "privy" &&
        saveThisAddress &&
        (selectedSavedAddressId === "new" || !selectedSavedAddressId) &&
        destinationAddressInput
      ) {
        try {
          const savedAddress = await WithdrawalAddressesApi.saveAddress(
            destinationAddressInput,
            addressLabel || "Saved Address"
          );
          setSavedAddresses((prev) => [savedAddress, ...prev]);
          toast.success("Address saved for future withdrawals");
        } catch (error) {
          // Don't block withdrawal if save fails
          console.error("Failed to save address:", error);
        }
      }

      await WithdrawalsApi.createWithdrawal(
        destAddress,
        withdrawAmount
      );

      toast.success(
        <span>
          Withdrawal of {""}
          <span className="text-emerald-400 font-semibold">
            ${withdrawAmount.toLocaleString()}
          </span>{" "}
          is being processed. It typically takes a few minutes to reach your
          wallet.
        </span>
      );

      queryClient.invalidateQueries({ queryKey: ["account-summary"] });
      queryClient.invalidateQueries({ queryKey: ["withdrawals"] });

      setAmount("");
      setWithdrawOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Withdrawal failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyAddress = async () => {
    const addressToCopy = authMethod === "wallet" ? publicKey?.toBase58() : null;
    if (!addressToCopy) return;
    try {
      await navigator.clipboard.writeText(addressToCopy);
      setWalletCopied(true);
      toast.success("Wallet address copied");
      setTimeout(() => setWalletCopied(false), 1500);
    } catch {
      toast.error("Failed to copy address");
    }
  };

  const handleCopyUserId = async () => {
    if (!effectiveUserId) return;
    try {
      await navigator.clipboard.writeText(effectiveUserId);
      setUidCopied(true);
      toast.success("UID copied");
      setTimeout(() => setUidCopied(false), 1500);
    } catch {
      toast.error("Failed to copy UID");
    }
  };

  const handleDisconnect = async () => {
    try {
      await logout();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to disconnect"
      );
    }
  };

  const handleExportWallet = async () => {
    if (authMethod !== "privy" || !privyWalletAddress) {
      toast.error("Export wallet is only available for Privy users");
      return;
    }

    try {
      // Use Privy's secure export wallet modal
      // This opens a secure iframe where the private key is displayed
      await privyExportWallet({ address: privyWalletAddress });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to export wallet"
      );
    }
  };

  // Show login button if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <Button
          variant="outline"
          onClick={() => setLoginModalOpen(true)}
          className="h-9 px-4 touch-manipulation"
        >
          Connect wallet / Login
        </Button>
        <LoginModal open={loginModalOpen} onOpenChange={setLoginModalOpen} />
      </>
    );
  }

  // Determine display name based on auth method
  const displayName = authMethod === "wallet"
    ? wallet?.adapter?.name || "Wallet"
    : authMethod === "privy"
      ? "Privy"
      : "Email";

  const displaySubtext = authMethod === "wallet" && publicKey
    ? formatWalletAddress(publicKey)
    : authMethod === "privy" && privyWalletAddress
      ? `${privyWalletAddress.slice(0, 4)}...${privyWalletAddress.slice(-4)}`
      : email
        ? email.length > 20 ? `${email.slice(0, 17)}...` : email
        : "Logged in";

  const closeMenu = () => setMenuOpen(false);

  // Check if the address is valid for withdrawal
  const isAddressValid = authMethod === "wallet"
    ? !!publicKey
    : authMethod === "privy"
      ? (selectedSavedAddressId && selectedSavedAddressId !== "new")
        ? true
        : isValidSolanaAddress(destinationAddressInput)
      : false;

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="px-3 py-1.5 rounded-md text-sm font-medium transition-colors min-w-[130px] h-auto flex flex-col items-start gap-0"
          >
            <span className="text-[9px] opacity-60 leading-tight font-normal">
              {authMethod === "wallet"
                ? `Connected via ${displayName}`
                : authMethod === "privy" && email
                  ? "Connected via Email"
                  : authMethod === "privy"
                    ? "Connected via Privy"
                    : "Connected"}
            </span>
            <span className="text-xs font-medium leading-tight tracking-wide">
              {displaySubtext}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72 p-0">
          {/* Overview Header */}
          <div className="px-4 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">
                Overview
              </div>
              <button
                type="button"
                onClick={togglePrivacyMode}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded hover:bg-accent/50 active:scale-95"
                title={privacyMode ? "Show balances" : "Hide balances"}
              >
                {privacyMode ? (
                  <EyeOffIcon className="size-3.5" />
                ) : (
                  <EyeIcon className="size-3.5" />
                )}
              </button>
            </div>

            {/* Main Balance Display */}
            <div className="space-y-1">
              <div className="text-2xl font-bold tracking-tight">
                {privacyMode ? "****" : formatCurrency(equityValue ?? 0, 2)}
                <span className="text-sm font-normal text-muted-foreground ml-1">USD</span>
              </div>
            </div>

            {/* User info with UID */}
            <div className="space-y-1.5">
              {/* Wallet Address - For wallet users */}
              {authMethod === "wallet" && publicKey && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground uppercase tracking-wide">Wallet</span>
                  <button
                    type="button"
                    onClick={handleCopyAddress}
                    className="flex items-center gap-1 text-foreground hover:text-primary transition active:scale-95"
                  >
                    <span>
                      {privacyMode ? "****" : formatWalletAddress(publicKey)}
                    </span>
                    {walletCopied ? (
                      <CheckIcon className="size-3 text-emerald-500" />
                    ) : (
                      <CopyIcon className="size-3" />
                    )}
                  </button>
                </div>
              )}

              {/* Email - For privy users OR wallet users with linked email */}
              {email && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground uppercase tracking-wide">Email</span>
                  <span className="text-foreground">
                    {privacyMode ? "****" : (email.length > 20 ? `${email.slice(0, 17)}...` : email)}
                  </span>
                </div>
              )}

              {/* UID */}
              {effectiveUserId && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground uppercase tracking-wide">UID</span>
                  <button
                    type="button"
                    onClick={handleCopyUserId}
                    className="flex items-center gap-1 text-foreground hover:text-primary transition active:scale-95"
                  >
                    <span>
                      {privacyMode ? "****" : `${effectiveUserId.slice(0, 6)}...${effectiveUserId.slice(-4)}`}
                    </span>
                    {uidCopied ? (
                      <CheckIcon className="size-3 text-emerald-500" />
                    ) : (
                      <CopyIcon className="size-3" />
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Deposit/Withdraw Actions */}
          <div className="px-4 pb-3 space-y-2">
            <div className="flex gap-2">
              <button
                type="button"
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium transition hover:bg-primary/90 focus-visible:outline-none"
                onClick={() => {
                  closeMenu();
                  onOpenDeposit?.();
                }}
              >
                <ArrowDownCircleIcon className="size-4" />
                Deposit
              </button>
              <button
                type="button"
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-secondary text-secondary-foreground px-4 py-2.5 text-sm font-medium transition hover:bg-secondary/80 focus-visible:outline-none"
                onClick={() => {
                  closeMenu();
                  setWithdrawOpen(true);
                }}
              >
                <ArrowUpCircleIcon className="size-4" />
                Withdraw
              </button>
            </div>
          </div>

          {/* Navigation & Settings */}
          <div className="border-t">
            <div className="px-2 py-2 space-y-1">
              <Link
                to="/account"
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-accent/60 transition"
                onClick={closeMenu}
              >
                <span className="flex items-center gap-2 font-medium">
                  <LayoutDashboardIcon className="size-4" />
                  Account
                </span>
              </Link>

              <Link
                to="/account?tab=transfers"
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-accent/60 transition"
                onClick={closeMenu}
              >
                <span className="flex items-center gap-2 font-medium">
                  <ArrowRightLeft className="size-4" />
                  Transfers
                </span>
              </Link>

              <Link
                to="/account?tab=history"
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-accent/60 transition"
                onClick={closeMenu}
              >
                <span className="flex items-center gap-2 font-medium">
                  <ClockIcon className="size-4" />
                  History
                </span>
              </Link>

              {authMethod === "privy" && (
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-accent/60 transition"
                  onClick={() => {
                    closeMenu();
                    privyFundWallet();
                  }}
                >
                  <span className="flex items-center gap-2 font-medium">
                    <CreditCardIcon className="size-4" />
                    Buy Crypto
                  </span>
                </button>
              )}

              {/* Export Wallet - Only for PURE Privy users, not linked wallet accounts */}
              {/* Linked accounts (loginMethod === 'both') have their own external wallet */}
              {authMethod === "privy" && loginMethod !== "both" && (
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-accent/60 transition"
                  onClick={() => {
                    closeMenu();
                    handleExportWallet();
                  }}
                >
                  <span className="flex items-center gap-2 font-medium">
                    <KeyIcon className="size-4" />
                    Export Wallet
                  </span>
                </button>
              )}

              {/* Link Email - For wallet users */}
              {authMethod === "wallet" && !email && (
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-accent/60 transition"
                  onClick={() => {
                    closeMenu();
                    setLinkEmailOpen(true);
                  }}
                >
                  <span className="flex items-center gap-2 font-medium">
                    <MailIcon className="size-4" />
                    Link Email for Mobile
                  </span>
                </button>
              )}

              {/* Show linked email status for wallet users with email */}
              {authMethod === "wallet" && email && (
                <div className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-2">
                    <MailIcon className="size-4" />
                    Email Linked
                  </span>
                  <span className="flex items-center gap-1 text-emerald-500 text-xs">
                    <CheckCircle2Icon className="size-3" />
                    Verified
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Sign Out */}
          <div className="border-t px-2 py-2">
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/60 transition"
              onClick={() => {
                closeMenu();
                handleDisconnect();
              }}
            >
              <LogOutIcon className="size-3.5" />
              {authMethod === "wallet" ? "Disconnect" : "Sign Out"}
            </button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent className="sm:max-w-[460px] px-6 bg-card" overlayClassName="bg-black/85">
          <DialogHeader>
            <DialogTitle>Withdraw Funds</DialogTitle>
          </DialogHeader>

          {/* Get current asset and chain info */}
          {(() => {
            const currentAsset = SUPPORTED_ASSETS.find(a => a.id === selectedAsset) || SUPPORTED_ASSETS[0];
            const currentChain = SUPPORTED_CHAINS.find(c => c.id === selectedChain) || SUPPORTED_CHAINS[0];
            const fundsLocked = (summary?.collateral ?? 0) - (summary?.availableBalance ?? 0);

            return (
              <div className="space-y-5 py-4">
                {/* Pending Withdrawal Status */}
                {pendingWithdrawal && (
                  <div className="rounded-lg border p-3" style={{ borderColor: 'rgba(46, 189, 133, 0.3)', backgroundColor: 'rgba(46, 189, 133, 0.08)' }}>
                    <div className="flex items-center gap-3">
                      <Loader2Icon className="size-4 animate-spin" style={{ color: SITE_GREEN }} />
                      <div className="flex-1">
                        <p className="text-sm font-medium" style={{ color: SITE_GREEN }}>
                          Withdrawal in progress...
                        </p>
                        <p className="text-xs text-muted-foreground">
                          ${parseFloat(pendingWithdrawal.amount).toFixed(2)} USDC → {pendingWithdrawal.destination_address.slice(0, 4)}...{pendingWithdrawal.destination_address.slice(-4)}
                        </p>
                      </div>
                      {pendingWithdrawal.tx_signature && (
                        <a
                          href={`https://solscan.io/tx/${pendingWithdrawal.tx_signature}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs hover:opacity-80 flex items-center gap-1"
                          style={{ color: SITE_GREEN }}
                        >
                          View <ExternalLink className="size-3" />
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* ASSET/CHAIN + INFO grouped together */}
                <div className="space-y-2">
                  {/* ASSET & CHAIN SELECTORS */}
                  <div className="flex gap-3">
                    {/* Asset Selector */}
                    <Select value={selectedAsset} onValueChange={setSelectedAsset}>
                      <SelectTrigger className="flex-1 h-10 bg-muted/30 border-border/50">
                        <div className="flex items-center justify-between w-full">
                          <span className="text-xs text-muted-foreground">Asset</span>
                          <div className="flex items-center gap-1.5">
                            <AssetIcon assetId={selectedAsset} className="size-5 rounded-full" />
                            <span className="font-medium text-sm">{currentAsset.symbol}</span>
                          </div>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {SUPPORTED_ASSETS.map(asset => (
                          <SelectItem
                            key={asset.id}
                            value={asset.id}
                            disabled={!asset.enabled}
                          >
                            <div className="flex items-center justify-between w-full gap-3">
                              <div className="flex items-center gap-2">
                                <AssetIcon assetId={asset.id} className="size-5 rounded-full" />
                                <span>{asset.symbol}</span>
                              </div>
                              {asset.comingSoon && (
                                <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                  Soon
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Chain Selector */}
                    <Select value={selectedChain} onValueChange={setSelectedChain}>
                      <SelectTrigger className="flex-1 h-10 bg-muted/30 border-border/50">
                        <div className="flex items-center justify-between w-full">
                          <span className="text-xs text-muted-foreground">Chain</span>
                          <div className="flex items-center gap-1.5">
                            <ChainIcon chainId={selectedChain} className="size-5" />
                            <span className="font-medium text-sm">{currentChain.name}</span>
                          </div>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {SUPPORTED_CHAINS.map(chain => (
                          <SelectItem
                            key={chain.id}
                            value={chain.id}
                            disabled={!chain.enabled}
                          >
                            <div className="flex items-center justify-between w-full gap-3">
                              <div className="flex items-center gap-2">
                                <ChainIcon chainId={chain.id} className="size-5" />
                                <span>{chain.name}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {chain.speed === "fast" && (
                                  <span className="text-[#2ebd85]">⚡⚡⚡</span>
                                )}
                                {chain.comingSoon && (
                                  <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                    Soon
                                  </span>
                                )}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* INFO ROW - Minimum Withdrawal on left (asset), Funds Locked on right */}
                  <div className="flex justify-between text-[11px] text-muted-foreground px-1">
                    <div className="flex items-center gap-1">
                      <span>Minimum Withdrawal</span>
                      <span className="text-foreground font-medium">10 {currentAsset.symbol}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>Funds Locked</span>
                      <span className="text-foreground font-medium">${formatCurrency(fundsLocked, 2)}</span>
                    </div>
                  </div>
                </div>

                {/* Amount Input - MOVED BEFORE DESTINATION */}
                <div className="space-y-2 pt-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Amount</label>
                    <button
                      type="button"
                      className="text-sm font-medium hover:opacity-80 cursor-pointer transition-opacity"
                      style={{ color: SITE_GREEN }}
                      onClick={() => setAmount((summary?.availableBalance ?? 0).toFixed(2))}
                      disabled={!summary?.availableBalance || summary.availableBalance <= 0}
                    >
                      Max
                    </button>
                  </div>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty or valid numbers with max 2 decimals
                      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                        setAmount(value);
                      }
                    }}
                    className="h-11 text-lg font-medium bg-muted/30 border-border/50 focus-visible:ring-1 focus-visible:ring-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    min="0"
                    step="0.01"
                  />
                  <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                    <span className="border-b border-dotted border-muted-foreground/50">Available</span>
                    <span className="text-foreground">{formatCurrency(summary?.availableBalance ?? 0, 2)} {currentAsset.symbol}</span>
                  </div>
                </div>

                {/* Destination Address Section - SIMPLIFIED */}
                <div className="space-y-2 pt-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Destination Wallet</label>
                    {/* Show Saved button only for privy users with saved addresses */}
                    {authMethod === "privy" && savedAddresses.length > 0 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="text-xs font-medium flex items-center gap-1 hover:opacity-80 transition-opacity"
                            style={{ color: SITE_GREEN }}
                          >
                            <BookmarkIcon className="size-3" />
                            Saved
                            <ChevronDownIcon className="size-3" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-2" align="end">
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground px-2 py-1">Saved Addresses</p>
                            {savedAddresses.map((addr) => (
                              <button
                                key={addr.id}
                                type="button"
                                onClick={() => {
                                  setDestinationAddressInput(addr.address);
                                  setSelectedSavedAddressId(addr.id);
                                }}
                                className="w-full text-left px-2 py-2 rounded-md hover:bg-muted/50 transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <StarIcon className="size-3 text-amber-500 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{addr.label}</p>
                                    <p className="text-xs text-muted-foreground truncate">
                                      {addr.address.slice(0, 8)}...{addr.address.slice(-8)}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>

                  {authMethod === "wallet" ? (
                    // Wallet users: show connected wallet address
                    <>
                      <div className="bg-muted/30 border border-border/50 rounded-lg py-2 px-3 text-sm break-all text-muted-foreground">
                        {publicKey?.toBase58() || "Connect wallet to withdraw"}
                      </div>
                      <p className="text-[11px] text-muted-foreground px-1">
                        Funds will be sent to your connected wallet.
                      </p>
                    </>
                  ) : (
                    // Email/Privy users: direct input with optional save
                    <>
                      {isLoadingAddresses ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2Icon className="size-4 animate-spin" />
                          Loading...
                        </div>
                      ) : (
                        <>
                          <Input
                            placeholder="Enter Solana wallet address"
                            value={destinationAddressInput}
                            onChange={(e) => {
                              setDestinationAddressInput(e.target.value);
                              setSelectedSavedAddressId("new");
                            }}
                            className="h-11 text-sm bg-muted/30 border-border/50 focus-visible:ring-1 focus-visible:ring-ring"
                          />
                          {destinationAddressInput && !isValidSolanaAddress(destinationAddressInput) && (
                            <p className="text-xs text-rose-500 px-1">
                              Please enter a valid Solana wallet address
                            </p>
                          )}

                          {/* Save address option - inline */}
                          {destinationAddressInput && isValidSolanaAddress(destinationAddressInput) && (
                            <div className="flex items-center gap-3 px-1">
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id="save-address"
                                  checked={saveThisAddress}
                                  onCheckedChange={(checked: boolean | "indeterminate") => setSaveThisAddress(checked === true)}
                                  className="size-3.5"
                                />
                                <label htmlFor="save-address" className="text-xs text-muted-foreground cursor-pointer">
                                  Save address
                                </label>
                              </div>
                              {saveThisAddress && (
                                <Input
                                  placeholder="Label (e.g., My Wallet)"
                                  value={addressLabel}
                                  onChange={(e) => setAddressLabel(e.target.value)}
                                  className="h-7 text-xs flex-1 bg-muted/30 border-border/50"
                                  maxLength={30}
                                />
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Status notice */}
                <p
                  className="text-[11px] flex items-center gap-1 px-1"
                  style={{ color: SITE_GREEN }}
                >
                  <CheckCircle2Icon className="size-3" />
                  Withdrawals typically complete within a few minutes.
                </p>
              </div>
            );
          })()}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setWithdrawOpen(false)}
              disabled={isLoading}
              className="flex-1 h-11"
            >
              Cancel
            </Button>
            <Button
              onClick={handleWithdraw}
              disabled={!amount || parseFloat(amount || "0") < MINIMUM_WITHDRAWAL || !isAddressValid || isLoading || !!pendingWithdrawal}
              className="flex-1 h-11 text-white"
              style={{ backgroundColor: SITE_GREEN }}
            >
              {isLoading ? (
                <>
                  <Loader2Icon className="size-4 animate-spin mr-2" />
                  Withdrawing...
                </>
              ) : pendingWithdrawal ? (
                <>Withdrawal in progress</>
              ) : (
                <>Withdraw ${parseFloat(amount || "0").toFixed(2)}</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Link Email Modal - For wallet users to add email for mobile access */}
      <LinkEmailModal
        open={linkEmailOpen}
        onOpenChange={setLinkEmailOpen}
        backendToken={accessToken}
        onSuccess={() => {
          // Email linked successfully - reload to refresh auth state
          // This is a one-time operation, so reload is acceptable UX
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }}
      />

    </>
  );
};
